import { useState, useEffect, useCallback } from 'react'
import { Search, ChevronLeft, ChevronRight, Loader2, Zap, Download, XCircle, Send, Clock, History, X, Undo2, MapPin, Camera } from 'lucide-react'
import toast from 'react-hot-toast'
import type { PickupStatus, VerificationMethod } from '../types'
import type { Pickup, PickupAssignment, AssignmentCandidate } from '../types'
import {
  fetchPickups, autoAssignPickups, cancelPickup, fetchAssignments, offerPickup,
  fetchPickupAssignments, revokePickupOffer, resolveDispute,
} from '../services/adminService'
import { exportToCsv } from '../utils/exportCsv'

/** The status tabs, plus one that is not a status: completed pickups the resident has contested. */
type StatusTab = PickupStatus | 'ALL' | 'DISPUTED'

const STATUS_TABS: { value: StatusTab; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'MISSED', label: 'Missed' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'DISPUTED', label: 'Disputed' },
]

const STATUS_STYLES: Record<string, { bg: string; text: string; dot: string }> = {
  COMPLETED: { bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500' },
  IN_PROGRESS: { bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500' },
  PENDING: { bg: 'bg-yellow-100', text: 'text-yellow-700', dot: 'bg-yellow-500' },
  SCHEDULED: { bg: 'bg-purple-100', text: 'text-purple-700', dot: 'bg-purple-500' },
  MISSED: { bg: 'bg-red-100', text: 'text-red-700', dot: 'bg-red-500' },
  CANCELLED: { bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400' },
}

const WASTE_STYLES: Record<string, string> = {
  GENERAL: 'bg-gray-100 text-gray-700',
  RECYCLABLE: 'bg-green-100 text-green-700',
  ORGANIC: 'bg-orange-100 text-orange-700',
  EWASTE: 'bg-purple-100 text-purple-700',
  BULK: 'bg-blue-100 text-blue-700',
}

const WASTE_LABELS: Record<string, string> = {
  GENERAL: 'General',
  RECYCLABLE: 'Recyclable',
  ORGANIC: 'Organic',
  EWASTE: 'E-Waste',
  BULK: 'Bulk',
}

/** Admin wording for each identifier the collector can present at confirm. */
const VERIFIED_VIA: Record<VerificationMethod, { badge: string; sentence: string }> = {
  PICKUP_QR: { badge: 'QR', sentence: 'Resident QR scanned' },
  BIN_QR: { badge: 'Bin code', sentence: 'Bin code scanned' },
  CODE: { badge: 'Code', sentence: 'Collection code entered' },
}

// Same palette as STATUS_STYLES: green for proof the resident took part in,
// blue for the system's own check, grey when the photo stands alone, red for
// a dispute.
const PROOF_STYLES = {
  identity: 'bg-green-100 text-green-700',
  location: 'bg-blue-100 text-blue-700',
  photoOnly: 'bg-gray-100 text-gray-600',
  disputed: 'bg-red-100 text-red-700',
}

interface ProofFact {
  badge: string
  sentence: string
  style: string
  title?: string
}

const PAGE_SIZE = 8

// The API cannot filter on disputes, so the Disputed tab pulls one page of the
// most recent completions and sifts it here; 100 is the largest page it allows.
const DISPUTE_SCAN_LIMIT = 100

// Mirrors PICKUP_MAX_OFFER_ROUNDS on the API; used only for the tooltip text.
const MAX_ROUNDS_HINT = 3

/** Minutes until an offer lapses, or null once it already has. */
function minutesLeft(expiresAt: string): number | null {
  const diff = new Date(expiresAt).getTime() - Date.now()
  return diff <= 0 ? null : Math.max(1, Math.round(diff / 60000))
}

/** What the API is asked for: the Disputed tab is a client-side sift over completed pickups. */
function serverStatus(tab: StatusTab): PickupStatus | undefined {
  if (tab === 'ALL') return undefined
  return tab === 'DISPUTED' ? 'COMPLETED' : tab
}

/**
 * The layers of proof behind a completed collection, strongest first. A row
 * completed before `verifiedVia` existed carries only the legacy `qrVerified`
 * flag, which always meant the resident's pickup QR. Nothing scanned and no
 * position leaves the photo as the only evidence, named as such so the gap
 * shows instead of hiding behind a green status.
 */
function proofFacts(pickup: Pickup): ProofFact[] {
  const method = pickup.verifiedVia ?? (pickup.qrVerified ? 'PICKUP_QR' : null)
  const facts: ProofFact[] = []
  if (method) {
    facts.push({ ...VERIFIED_VIA[method], style: PROOF_STYLES.identity })
  }
  if (pickup.locationVerified) {
    const distance = pickup.confirmDistanceM != null ? `${Math.round(pickup.confirmDistanceM)} m` : null
    facts.push({
      badge: 'Location',
      sentence: distance ? `Location verified (${distance})` : 'Location verified',
      style: PROOF_STYLES.location,
      title: distance ? `${distance} from the address` : undefined,
    })
  }
  if (facts.length === 0) {
    facts.push(
      pickup.photoUrl
        ? { badge: 'Photo only', sentence: 'Photo only', style: PROOF_STYLES.photoOnly }
        : { badge: 'No proof', sentence: 'No proof recorded', style: PROOF_STYLES.photoOnly },
    )
  }
  return facts
}

/** "Disputed on {date}: {reason}", or without the reason when the resident gave none. */
function disputeSentence(pickup: Pickup): string | null {
  if (!pickup.disputedAt) return null
  const when = new Date(pickup.disputedAt).toLocaleString()
  return pickup.disputeReason
    ? `Disputed on ${when}: ${pickup.disputeReason}`
    : `Disputed on ${when} — no reason given`
}

/** The proof pills a completed row carries under its status, plus the dispute flag. */
function VerificationBadges({ pickup }: { pickup: Pickup }) {
  const facts = pickup.status === 'COMPLETED' ? proofFacts(pickup) : []
  if (facts.length === 0 && !pickup.disputedAt) return null
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {facts.map((fact) => (
        <span
          key={fact.badge}
          title={fact.title}
          className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap ${fact.style}`}
        >
          {fact.badge}
        </span>
      ))}
      {pickup.disputedAt && (
        <span
          title={pickup.disputeReason ?? 'The resident reported that this collection did not happen as recorded'}
          className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap ${PROOF_STYLES.disputed}`}
        >
          Disputed
        </span>
      )}
    </div>
  )
}

export default function Pickups() {
  const [pickups, setPickups] = useState<Pickup[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusTab>('ALL')
  const [page, setPage] = useState(1)
  const [offers, setOffers] = useState<Record<string, PickupAssignment>>({})
  const [offering, setOffering] = useState<string | null>(null)
  const [historyFor, setHistoryFor] = useState<string | null>(null)
  const [history, setHistory] = useState<PickupAssignment[]>([])
  const [candidates, setCandidates] = useState<AssignmentCandidate[]>([])
  const [historyMeta, setHistoryMeta] = useState<{
    roundsUsed: number
    maxOfferRounds: number
    shortlistSize: number
    escalatedAt: string | null
    escalationReason: string | null
  }>({ roundsUsed: 0, maxOfferRounds: 3, shortlistSize: 3, escalatedAt: null, escalationReason: null })
  const [historyLoading, setHistoryLoading] = useState(false)

  const loadPickups = useCallback(async () => {
    setLoading(true)
    try {
      const disputedOnly = statusFilter === 'DISPUTED'
      const data = await fetchPickups({
        status: serverStatus(statusFilter),
        page: disputedOnly ? 1 : page,
        limit: disputedOnly ? DISPUTE_SCAN_LIMIT : PAGE_SIZE,
      })
      const rows: Pickup[] = data?.pickups ?? data ?? []
      if (disputedOnly) {
        // Every match is held at once and paged client-side further down.
        const disputed = rows.filter((p) => p.disputedAt)
        setPickups(disputed)
        setTotal(disputed.length)
      } else {
        setPickups(rows)
        setTotal(data?.pagination?.total ?? data?.total ?? 0)
      }
    } catch {
      toast.error('Failed to load pickups')
    } finally {
      setLoading(false)
    }
  }, [statusFilter, page])

  // Outstanding offers, keyed by pickup, so a pending row can say who is
  // deciding on it rather than just reading "Unassigned".
  const loadOffers = useCallback(async () => {
    try {
      const data = await fetchAssignments({ status: 'OFFERED', limit: 100 })
      const byPickup: Record<string, PickupAssignment> = {}
      for (const assignment of (data?.assignments ?? []) as PickupAssignment[]) {
        byPickup[assignment.pickupId] = assignment
      }
      setOffers(byPickup)
    } catch {
      setOffers({})
    }
  }, [])

  useEffect(() => { loadPickups() }, [loadPickups])
  useEffect(() => { loadOffers() }, [loadOffers])

  const refresh = useCallback(() => {
    loadPickups()
    loadOffers()
  }, [loadPickups, loadOffers])

  const openHistory = useCallback(async (pickupId: string) => {
    setHistoryFor(pickupId)
    setHistoryLoading(true)
    try {
      const data = await fetchPickupAssignments(pickupId)
      setHistory(data?.assignments ?? [])
      setCandidates(data?.candidates ?? [])
      setHistoryMeta({
        roundsUsed: data?.roundsUsed ?? 0,
        maxOfferRounds: data?.maxOfferRounds ?? 3,
        shortlistSize: data?.shortlistSize ?? 3,
        escalatedAt: data?.pickup?.escalatedAt ?? null,
        escalationReason: data?.pickup?.escalationReason ?? null,
      })
    } catch {
      toast.error('Failed to load the offer history')
      setHistoryFor(null)
    } finally {
      setHistoryLoading(false)
    }
  }, [])

  const handleTargetedOffer = async (pickupId: string, collectorId: string, name: string) => {
    setOffering(pickupId)
    try {
      await offerPickup(pickupId, collectorId)
      toast.success(`Offered to ${name}.`)
      await openHistory(pickupId)
      refresh()
    } catch (err) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      toast.error(message || 'Failed to offer the pickup')
    } finally {
      setOffering(null)
    }
  }

  const handleRevoke = async (pickupId: string, reoffer: boolean) => {
    setOffering(pickupId)
    try {
      await revokePickupOffer(pickupId, reoffer)
      toast.success(reoffer ? 'Passed on to another collector.' : 'Offer withdrawn.')
      if (historyFor === pickupId) await openHistory(pickupId)
      refresh()
    } catch (err) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      toast.error(message || 'Failed to withdraw the offer')
    } finally {
      setOffering(null)
    }
  }

  const handleOffer = async (pickupId: string) => {
    setOffering(pickupId)
    try {
      await offerPickup(pickupId)
      toast.success('Pickup offered to the best-matched collector.')
      refresh()
    } catch (err) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      toast.error(message || 'Failed to offer pickup')
    } finally {
      setOffering(null)
    }
  }

  const totalPages = Math.ceil(total / PAGE_SIZE)
  const disputedTab = statusFilter === 'DISPUTED'

  // Client-side search filter on loaded data
  const filtered = pickups.filter((p) => {
    if (!search) return true
    const q = search.toLowerCase()
    return (
      p.id.toLowerCase().includes(q) ||
      (p.resident?.name?.toLowerCase().includes(q) ?? false) ||
      (p.collector?.name?.toLowerCase().includes(q) ?? false) ||
      p.address.toLowerCase().includes(q)
    )
  })

  // The Disputed tab holds every match at once, so its pages are cut here;
  // every other tab is already one server page.
  const visible = disputedTab ? filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE) : filtered

  // The drawer shows the verification of the row it was opened from.
  const historyPickup = pickups.find((p) => p.id === historyFor) ?? null

  const handleAutoAssign = async () => {
    if (!confirm('Offer every pending pickup to the best-matched collector?')) return
    try {
      const result = await autoAssignPickups()
      const offered = result?.offered ?? result?.assigned ?? 0
      // Say why each was skipped: one already with a collector is not one
      // nobody can take.
      const reasons: Record<string, number> = result?.reasons ?? {}
      const LABELS: Record<string, string> = {
        already_offered: 'already with a collector',
        rounds_exhausted: 'need manual assignment',
        already_approached: 'already asked every nearby collector',
        no_candidate: 'had no collector in range',
        pickup_not_offerable: 'can no longer be offered',
      }
      const parts = Object.entries(reasons)
        .filter(([, n]) => n > 0)
        .map(([code, n]) => `${n} ${LABELS[code] ?? code.replace(/_/g, ' ')}`)
      if (parts.length > 0) {
        toast(`${offered} pickup(s) offered. ${parts.join('; ')}.`, { icon: '\u26A0\uFE0F', duration: 8000 })
      } else {
        toast.success(`${offered} pickup(s) offered to collectors.`)
      }
      refresh()
    } catch {
      toast.error('Auto-assign failed')
    }
  }

  return (
    <div className="space-y-5">
      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { label: disputedTab ? 'Disputed' : 'Total Pickups', value: total, color: 'text-blue-600 bg-blue-50' },
          { label: 'Showing', value: visible.length, color: 'text-green-700 bg-green-50' },
          { label: 'Page', value: `${page} / ${totalPages || 1}`, color: 'text-yellow-700 bg-yellow-50' },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <p className="text-xs text-[#6B7280] font-medium">{label}</p>
            <p className={`text-2xl font-bold mt-1 ${color.split(' ')[0]}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 flex items-center gap-2 bg-[#F8FAF9] border border-gray-200 rounded-xl px-3 py-2">
            <Search className="w-4 h-4 text-[#6B7280]" />
            <input
              type="text"
              placeholder="Search by ID, resident, collector, or address..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 bg-transparent text-sm text-[#1A1A2E] placeholder-[#6B7280] outline-none"
            />
          </div>
          <button
            onClick={handleAutoAssign}
            className="flex items-center gap-1.5 text-xs font-semibold text-white bg-primary-600 px-4 py-2 rounded-xl hover:bg-primary-700 transition-colors whitespace-nowrap"
          >
            <Zap size={13} />
            Auto-Assign
          </button>
          <button
            type="button"
            onClick={() => {
              if (!pickups.length) return
              exportToCsv(
                ['ID', 'Resident', 'Address', 'Waste Type', 'Status', 'Scheduled Date', 'Amount'],
                pickups.map((p: Pickup) => [
                  p.id,
                  p.resident?.name ?? '',
                  p.address ?? '',
                  p.wasteType ?? '',
                  p.status,
                  p.scheduledDate ? new Date(p.scheduledDate).toLocaleDateString() : '',
                  p.payment?.amount != null ? String(p.payment.amount) : '',
                ]),
                'pickups'
              )
            }}
            className="flex items-center gap-1.5 text-xs font-semibold text-[#1A1A2E] bg-gray-100 px-4 py-2 rounded-xl hover:bg-gray-200 transition-colors whitespace-nowrap"
          >
            <Download size={13} />
            Export CSV
          </button>
        </div>

        {/* Status Tabs */}
        <div className="flex flex-wrap gap-1.5">
          {STATUS_TABS.map(({ value, label }) => (
            <button
              key={value}
              onClick={() => { setStatusFilter(value); setPage(1) }}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap ${
                statusFilter === value
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'bg-[#F8FAF9] text-[#6B7280] hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        {disputedTab && (
          <p className="text-[11px] text-[#9CA3AF]">
            Completed pickups the resident has contested, among the {DISPUTE_SCAN_LIMIT} most recent completions.
          </p>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <p className="text-sm font-semibold text-[#1A1A2E]">
            {total} pickup{total !== 1 ? 's' : ''}
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-[#F8FAF9]">
                  {['ID', 'Resident', 'Collector', 'Waste Type', 'Address', 'Date', 'Status', 'Offer', 'Actions'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-[#6B7280] whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {visible.map((pickup) => {
                  const st = STATUS_STYLES[pickup.status] ?? STATUS_STYLES.CANCELLED
                  const wt = WASTE_STYLES[pickup.wasteType] ?? 'bg-gray-100 text-gray-700'
                  const wasteLabel = WASTE_LABELS[pickup.wasteType] ?? pickup.wasteType
                  const offer = offers[pickup.id]
                  const remaining = offer ? minutesLeft(offer.expiresAt) : null
                  return (
                    <tr key={pickup.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3 text-xs font-mono font-semibold text-primary-700">{pickup.id.slice(0, 8)}</td>
                      <td className="px-4 py-3 text-xs font-medium text-[#1A1A2E] whitespace-nowrap">
                        {pickup.resident?.name ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-xs text-[#6B7280] whitespace-nowrap">
                        {pickup.collector?.name ?? <span className="italic text-gray-400">Unassigned</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${wt}`}>
                          {wasteLabel}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-[#6B7280] max-w-[160px] truncate">{pickup.address}</td>
                      <td className="px-4 py-3 text-xs text-[#6B7280] whitespace-nowrap">
                        {new Date(pickup.scheduledDate).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`flex items-center gap-1.5 text-xs font-semibold px-2 py-0.5 rounded-full w-fit ${st.bg} ${st.text}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                          {pickup.status.replace('_', ' ')}
                        </span>
                        <VerificationBadges pickup={pickup} />
                      </td>
                      <td className="px-4 py-3 text-xs whitespace-nowrap">
                        {offer ? (
                          <button
                            type="button"
                            onClick={() => openHistory(pickup.id)}
                            className="flex items-center gap-1.5 font-semibold text-orange-700 bg-orange-50 px-2 py-0.5 rounded-full w-fit hover:bg-orange-100 transition"
                            title={`Round ${offer.round} of ${MAX_ROUNDS_HINT} — open the offer history`}
                          >
                            <Clock className="w-3 h-3" />
                            {offer.collector?.name ?? 'Collector'}
                            {remaining != null ? ` · ${remaining}m left` : ' · expiring'}
                          </button>
                        ) : pickup.status === 'PENDING' && !pickup.collectorId ? (
                          <button
                            type="button"
                            onClick={() => openHistory(pickup.id)}
                            className="italic text-gray-400 hover:text-primary-700 transition"
                            title="Open the offer history"
                          >
                            Not offered
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => openHistory(pickup.id)}
                            className="text-gray-300 hover:text-primary-700 transition"
                            title="Open the offer history"
                          >
                            <History className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {pickup.status === 'PENDING' && !pickup.collectorId && !offer && (
                          <button
                            type="button"
                            title="Offer to the best-matched collector"
                            disabled={offering === pickup.id}
                            onClick={() => handleOffer(pickup.id)}
                            className="flex items-center gap-1 text-xs text-primary-700 hover:bg-primary-50 px-2 py-1 rounded-lg transition disabled:opacity-50"
                          >
                            <Send className="w-3.5 h-3.5" />
                            {offering === pickup.id ? 'Offering...' : 'Offer'}
                          </button>
                        )}
                        {!['COMPLETED', 'CANCELLED'].includes(pickup.status) && (
                          <button
                            type="button"
                            title="Cancel pickup"
                            className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 px-2 py-1 rounded-lg transition"
                            onClick={async () => {
                              const reason = prompt('Reason for cancellation (optional):')
                              if (reason === null) return
                              try {
                                const result = await cancelPickup(pickup.id, reason || undefined)
                                // A paid pickup leaves money owed: say so, rather than a bare "cancelled".
                                if (result?.refundNeeded) {
                                  toast.success('Pickup cancelled. The resident is owed a refund: refund it from Payments.', { duration: 8000 })
                                } else {
                                  toast.success('Pickup cancelled')
                                }
                                refresh()
                              } catch {
                                toast.error('Failed to cancel pickup')
                              }
                            }}
                          >
                            <XCircle className="w-3.5 h-3.5" />
                            Cancel
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
                {visible.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-4 py-8 text-center text-sm text-[#6B7280]">
                      No pickups found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Offer history */}
      {historyFor && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white">
              <div>
                <h3 className="text-lg font-bold text-[#1A1A2E]">Offer history</h3>
                <p className="text-xs text-[#6B7280]">
                  Pickup {historyFor.slice(0, 8)} — round {historyMeta.roundsUsed} of{' '}
                  {historyMeta.maxOfferRounds} used
                </p>
              </div>
              <button
                type="button"
                onClick={() => setHistoryFor(null)}
                className="p-1 rounded-lg hover:bg-gray-100 text-[#6B7280]"
                title="Close"
              >
                <X size={18} />
              </button>
            </div>

            {historyLoading ? (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
              </div>
            ) : (
              <div className="p-5 space-y-5">
                {historyPickup && (historyPickup.status === 'COMPLETED' || !!historyPickup.disputedAt) && (
                  <div>
                    <p className="text-xs font-bold text-[#6B7280] uppercase tracking-wide mb-2">
                      Verification
                    </p>
                    {historyPickup.status === 'COMPLETED' && (
                      <p className="text-sm text-[#1A1A2E]">
                        {proofFacts(historyPickup).map((fact) => fact.sentence).join(' · ')}
                        {historyPickup.photoUrl && (
                          <a
                            href={historyPickup.photoUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="ml-2 inline-flex items-center gap-1 text-xs font-semibold text-primary-700 hover:underline"
                          >
                            <Camera size={12} />
                            Proof photo
                          </a>
                        )}
                      </p>
                    )}
                    {historyPickup.disputedAt && (
                      <p className="text-sm font-semibold text-red-600 mt-1">{disputeSentence(historyPickup)}</p>
                    )}
                    {historyPickup.disputedAt && historyPickup.disputeResolvedAt && (
                      <p className="text-xs text-[#1A1A2E] mt-1">
                        Resolved {new Date(historyPickup.disputeResolvedAt).toLocaleString()}: {historyPickup.disputeResolution}
                      </p>
                    )}
                    {historyPickup.disputedAt && !historyPickup.disputeResolvedAt && (
                      <div className="flex gap-2 mt-2">
                        {(['UPHELD', 'REJECTED'] as const).map((outcome) => (
                          <button
                            key={outcome}
                            type="button"
                            className={`text-xs font-semibold px-3 py-1.5 rounded-lg border ${outcome === 'UPHELD' ? 'border-red-300 text-red-700 hover:bg-red-50' : 'border-gray-300 text-[#1A1A2E] hover:bg-gray-50'}`}
                            onClick={async () => {
                              const note = prompt(
                                outcome === 'UPHELD'
                                  ? 'Uphold: the collection did not happen as claimed. The collector is not paid for this job. Note for the resident:'
                                  : 'Reject: the collection happened. Note for the resident:',
                              )
                              if (note === null) return
                              try {
                                const result = await resolveDispute(historyPickup.id, outcome, note)
                                toast.success(
                                  result?.refundPaymentId
                                    ? 'Dispute upheld. Now refund the payment from Payments.'
                                    : outcome === 'UPHELD' ? 'Dispute upheld.' : 'Dispute rejected.',
                                  { duration: 8000 },
                                )
                                refresh()
                              } catch (err) {
                                const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
                                toast.error(message ?? 'Could not resolve the dispute')
                              }
                            }}
                          >
                            {outcome === 'UPHELD' ? 'Uphold' : 'Reject'}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {historyMeta.escalatedAt && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5">
                    <p className="text-xs font-bold text-amber-900">
                      Waiting for you since{' '}
                      {new Date(historyMeta.escalatedAt).toLocaleString()}
                    </p>
                    <p className="text-[11px] text-amber-800 mt-0.5">
                      {historyMeta.escalationReason ??
                        'The offer round ended without anyone taking this pickup.'}{' '}
                      Nobody else can be reached automatically — pick someone below.
                    </p>
                  </div>
                )}

                <div>
                  <p className="text-xs font-bold text-[#6B7280] uppercase tracking-wide mb-2">
                    Who has been asked
                  </p>
                  {history.length === 0 ? (
                    <p className="text-sm text-[#9CA3AF] italic">
                      Nobody yet — this pickup has not been offered to anyone.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {history.map((a) => (
                        <div key={a.id} className="flex items-center justify-between gap-3 border border-gray-100 rounded-xl px-3 py-2">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-[#1A1A2E] truncate">
                              {a.collector?.name ?? 'Collector'}
                              <span className="ml-2 text-[10px] font-normal text-[#9CA3AF]">
                                round {a.round}
                                {a.distanceKm != null ? ` · ${a.distanceKm} km` : ''}
                              </span>
                            </p>
                            <p className="text-[11px] text-[#6B7280]">
                              Offered {new Date(a.offeredAt).toLocaleString()}
                              {a.respondedAt ? ` · answered ${new Date(a.respondedAt).toLocaleString()}` : ''}
                            </p>
                            {a.declineReason && (
                              <p className="text-[11px] text-red-600 mt-0.5">{a.declineReason}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                              {a.status.charAt(0) + a.status.slice(1).toLowerCase()}
                            </span>
                            {a.status === 'OFFERED' && (
                              <button
                                type="button"
                                disabled={offering === historyFor}
                                onClick={() => handleRevoke(historyFor, false)}
                                className="flex items-center gap-1 text-[11px] font-semibold text-red-600 bg-red-50 border border-red-200 px-2 py-1 rounded-lg hover:bg-red-100 transition disabled:opacity-50"
                              >
                                <Undo2 size={11} />
                                Withdraw
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <p className="text-xs font-bold text-[#6B7280] uppercase tracking-wide mb-2">
                    Nearest available collectors
                  </p>
                  <p className="text-[11px] text-[#9CA3AF] mb-2">
                    The first {historyMeta.shortlistSize} are the shortlist an escalation puts in
                    front of you. Nobody here has been asked about this pickup yet.
                  </p>
                  {candidates.length === 0 ? (
                    <p className="text-sm text-[#9CA3AF] italic">
                      No eligible collector — everyone is off duty, at capacity, out of range, or has
                      already been asked.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {candidates.map((c, index) => (
                        <div key={c.collectorId} className="flex items-center justify-between gap-3 border border-gray-100 rounded-xl px-3 py-2">
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-[#1A1A2E] truncate">
                              <span
                                className={`mr-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded ${
                                  index < historyMeta.shortlistSize
                                    ? 'bg-primary-100 text-primary-700'
                                    : 'bg-gray-100 text-gray-700'
                                }`}
                              >
                                {index + 1}
                              </span>
                              {c.name}
                            </p>
                            <p className="text-[11px] text-[#6B7280] flex flex-wrap items-center gap-x-2">
                              <span>score {c.score}</span>
                              {c.distanceKm != null && <span>{c.distanceKm} km</span>}
                              <span>{c.dayLoad}/{c.maxDailyPickups} booked</span>
                              {c.ratingCount > 0 && <span>{c.averageRating}★ ({c.ratingCount})</span>}
                              {c.companyName && (
                                <span className="text-purple-700">{c.companyName}</span>
                              )}
                              {c.zoneMatch && (
                                <span className="inline-flex items-center gap-0.5 text-blue-700">
                                  <MapPin size={9} /> zone
                                </span>
                              )}
                              {c.companyZoneMatch && <span className="text-blue-700">contracted</span>}
                              {c.reachable === false && (
                                <span className="text-amber-700" title="No device registered for push, or pickup alerts switched off — phone them">
                                  no push
                                </span>
                              )}
                            </p>
                          </div>
                          <button
                            type="button"
                            disabled={offering === historyFor}
                            onClick={() => handleTargetedOffer(historyFor, c.collectorId, c.name)}
                            className="flex items-center gap-1 text-[11px] font-semibold text-primary-700 bg-primary-50 border border-primary-200 px-2 py-1 rounded-lg hover:bg-primary-100 transition disabled:opacity-50 flex-shrink-0"
                          >
                            <Send size={11} />
                            Offer
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pagination */}
        {totalPages > 0 && (
          <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
            <p className="text-xs text-[#6B7280]">
              Showing {Math.min((page - 1) * PAGE_SIZE + 1, total)}–{Math.min(page * PAGE_SIZE, total)} of {total}
            </p>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-lg text-[#6B7280] hover:bg-gray-100 disabled:opacity-40 transition-colors"
              >
                <ChevronLeft size={16} />
              </button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map((p) => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`w-7 h-7 rounded-lg text-xs font-semibold transition-colors ${
                    p === page ? 'bg-primary-600 text-white' : 'text-[#6B7280] hover:bg-gray-100'
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages || totalPages === 0}
                className="p-1.5 rounded-lg text-[#6B7280] hover:bg-gray-100 disabled:opacity-40 transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
